import { formatForDateInput } from "./dateUtils";
import { databaseSubscriptionToSubscription } from "./dataTransformers";
import { type DatabaseSubscription } from "@/hooks/useSubscriptions";
import { type Subscription } from "@/types/subscription";

export interface SubscriptionFormData {
  // Essentials
  name: string;
  // amount is optional to support PAYG subscriptions
  amount: number;
  currency: string;
  // frequency is optional when billingType === 'pay_as_you_go'
  frequency: string;
  customFrequencyDays: number;
  nextPayment: string;
  startDate: string;
  status: string;
  // Billing / Provider
  autoRenew: boolean;
  autoRenewalDate: string;
  provider: string;
  websiteUrl: string;
  // New: billing type selector to choose between recurring and pay-as-you-go
  billingType: "recurring" | "pay_as_you_go";
  // Lifecycle
  cancellationDate: string;
  trialEnd: string;
  pauseDate: string;
  resumeDate: string;
  // Preferences
  paymentMethod: string;
  sentiment: number;
  // Notes / Misc
  notes: string;
  reconciliationStatus: string;
}

export const createInitialFormData = (
  subscription?: Subscription | DatabaseSubscription,
): SubscriptionFormData => {
  const normalizedSubscription: Subscription | undefined = !subscription
    ? undefined
    : "billing_cycle" in subscription
      ? databaseSubscriptionToSubscription(subscription)
      : subscription;

  return {
  // Essentials
  name: normalizedSubscription?.name || "",
  amount: normalizedSubscription?.amount || 0,
  currency: normalizedSubscription?.currency || "EUR",
  frequency: normalizedSubscription?.frequency || "monthly",
  customFrequencyDays: normalizedSubscription?.customFrequencyDays || 30,
  nextPayment: normalizedSubscription?.nextPayment
    ? formatForDateInput(new Date(normalizedSubscription.nextPayment))
    : formatForDateInput(new Date()),
  startDate: normalizedSubscription?.startDate
    ? formatForDateInput(new Date(normalizedSubscription.startDate))
    : formatForDateInput(new Date()),
  status: normalizedSubscription?.status || "active",
  // Billing / Provider
  autoRenew:
    typeof normalizedSubscription?.autoRenew === "boolean"
      ? normalizedSubscription.autoRenew
      : true,
  autoRenewalDate: normalizedSubscription?.autoRenewalDate
    ? formatForDateInput(new Date(normalizedSubscription.autoRenewalDate))
    : "",
  provider: normalizedSubscription?.provider || "",
  websiteUrl: normalizedSubscription?.websiteUrl || "",
  // Billing type: default to 'recurring' for backwards compatibility
  billingType: ((normalizedSubscription as any)?.billingType ??
    "recurring") as SubscriptionFormData["billingType"],
  // Lifecycle
  cancellationDate: normalizedSubscription?.cancellationDate
    ? formatForDateInput(new Date(normalizedSubscription.cancellationDate))
    : "",
  trialEnd: (() => {
    const s = normalizedSubscription as any;
    const val = s?.trialEndDate ?? s?.trial_end_date;
    return val ? formatForDateInput(new Date(val)) : "";
  })(),
  pauseDate: (() => {
    const s = normalizedSubscription as any;
    const val = s?.pauseDate ?? s?.pause_date;
    return val ? formatForDateInput(new Date(val)) : "";
  })(),
  resumeDate: (() => {
    const s = normalizedSubscription as any;
    const val = s?.resumeDate ?? s?.resume_date;
    return val ? formatForDateInput(new Date(val)) : "";
  })(),
  // Preferences
  paymentMethod: normalizedSubscription?.paymentMethod || "",
  sentiment: normalizedSubscription?.sentiment || 3,
  // Notes / Misc
  notes: normalizedSubscription?.notes || "",
  reconciliationStatus: normalizedSubscription?.reconciliationStatus || "pending",
  };
};

export const resetFormData = (): SubscriptionFormData => ({
  name: "",
  amount: 0,
  currency: "EUR",
  frequency: "monthly",
  customFrequencyDays: 30,
  nextPayment: formatForDateInput(new Date()),
  startDate: formatForDateInput(new Date()),
  status: "active",
  autoRenew: true,
  autoRenewalDate: "",
  provider: "",
  websiteUrl: "",
  // default billing type for new forms
  billingType: "recurring",
  cancellationDate: "",
  pauseDate: "",
  resumeDate: "",
  paymentMethod: "",
  sentiment: 3,
  notes: "",
  reconciliationStatus: "pending",
  trialEnd: "",
});

export const updateFormData = <K extends keyof SubscriptionFormData>(
  prev: SubscriptionFormData,
  key: K,
  value: SubscriptionFormData[K],
): SubscriptionFormData => ({
  ...prev,
  [key]: value,
});
